/**
 * V2.0.80 — Opening (door / window) drag-placement mode extracted from
 * FloorPlan2D.jsx.
 *
 * This is the state machine for the "just added a door — drag it along
 * the wall to position it, click to commit" interaction. Flow:
 *
 *   1. `WallAddMenu` inserts a new door / window into `floor.doors[]`
 *      or `floor.windows[]` and calls `beginDrag(config)`.
 *   2. `handlePointerMove(e)` fires on every mousemove: it clamps the
 *      cursor to the wall span (leaving a 40cm margin from each end),
 *      recomputes the opening's `position` field, and pushes the
 *      updated blueprint through `onPreviewBlueprint` (in-memory only,
 *      no PUT). This gives the user real-time visual feedback.
 *   3. `handleClick()` on the canvas commits the current position —
 *      the parent's own click handler still fires the final
 *      `onUpdateBlueprint` (via the normal `commit` path). Here we
 *      just wipe the mode so subsequent moves fall through.
 *   4. `active` is exposed so cursor style + hover-wall guards can
 *      short-circuit while a drag is in flight.
 *
 * Escape handling lives in `useFloorPlanShortcuts` — it clears the
 * mode via `setMode(null)` (kept exposed on the return value for that
 * hook's convenience).
 */
import { useCallback, useRef, useState } from "react";
import { emitInteractionEvent } from "../../lib/interactionTelemetry";

export default function useOpeningDragMode({
  blueprint,
  floorIndex,
  pointerToWorld,
  onPreviewBlueprint,
}) {
  const [mode, setMode] = useState(null);
  const dragStartMsRef = useRef(null);

  const beginDrag = useCallback((config) => {
    setMode(config);
    dragStartMsRef.current = Date.now();
    emitInteractionEvent({
      tool: "opening_drag",
      action: "begin",
      meta: { kind: config?.kind || null },
    });
  }, []);

  const cancel = useCallback(() => {
    if (mode) {
      emitInteractionEvent({
        tool: "opening_drag",
        action: "cancel",
        ms: dragStartMsRef.current ? Date.now() - dragStartMsRef.current : null,
        committed: false,
      });
    }
    setMode(null);
    dragStartMsRef.current = null;
  }, [mode]);

  const handlePointerMove = useCallback(
    (e) => {
      if (!mode || !blueprint) return false;
      const world = pointerToWorld(e);
      if (!world) return true;
      const cursorAlong = mode.axis === "x" ? world.x_m : world.z_m;
      const span = mode.span_hi - mode.span_lo;
      if (span <= 0) return true;
      // Reserve ~0.4m from each end so the opening doesn't clip the corners.
      const margin = 0.4;
      const lo = mode.span_lo + margin;
      const hi = mode.span_hi - margin;
      const clamped = Math.min(hi, Math.max(lo, cursorAlong));
      const newPos = (clamped - mode.span_lo) / span;
      const key = mode.kind === "window" ? "windows" : "doors";
      const next = JSON.parse(JSON.stringify(blueprint));
      const f = (next.floors || [])[floorIndex - 1];
      if (!f || !Array.isArray(f[key]) || mode.idx >= f[key].length) return true;
      f[key][mode.idx] = { ...f[key][mode.idx], position: newPos };
      // Route through the preview channel so we don't PUT on every frame;
      // the next click commits via onUpdateBlueprint upstream.
      onPreviewBlueprint?.(next);
      return true;
    },
    [mode, blueprint, floorIndex, pointerToWorld, onPreviewBlueprint],
  );

  const handleClick = useCallback(() => {
    if (!mode) return false;
    emitInteractionEvent({
      tool: "opening_drag",
      action: "commit",
      ms: dragStartMsRef.current ? Date.now() - dragStartMsRef.current : null,
      committed: true,
      meta: { kind: mode.kind },
    });
    setMode(null);
    dragStartMsRef.current = null;
    return true; // caller should treat click as consumed
  }, [mode]);

  return {
    active: !!mode,
    mode,
    setMode, // exposed for useFloorPlanShortcuts (Escape handling)
    beginDrag,
    cancel,
    handlePointerMove,
    handleClick,
  };
}
